import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { mercuryContract } from "@common/contracts/mercury.contract";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { integrationConnectionWithAccountsResponseSchema } from "@common/schemas/domains/integrationConnection/integration-connection-response.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { useConnectAccount } from "@components/connectAccount/ConnectAccount.context";
import { useConnectTarget } from "@hooks/useConnectTarget";
import { addIntegrationConnectionInQueryData } from "@queries/entitiesAccounts";
import { invalidateAccountRelatedQueries } from "./account.api";
import { integrationConnectionQueryKeys } from "./integrationConnection.api";

const mercuryContractClient = getBackendAPIClient(mercuryContract);

export function useMercuryConnectionCreationMutation(
    onSuccess?: (connection: IntegrationConnection) => void,
) {
    const { workspaceId, isOrgConnect, refreshConnections } =
        useConnectTarget();
    const { updateConnectAccountState } = useConnectAccount();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            data: Omit<
                ServerInferRequest<
                    typeof mercuryContract.createConnection
                >["body"],
                "workspaceId"
            >,
        ) => {
            if (!workspaceId && !isOrgConnect) {
                throw new Error("No active workspace");
            }

            const response = await mercuryContractClient.createConnection({
                body: {
                    ...data,
                    workspaceId,
                },
            });

            return integrationConnectionWithAccountsResponseSchema.parse(
                response.body,
            );
        },
        onMutate: () => {
            updateConnectAccountState({
                isConnecting: true,
            });
        },
        onSuccess: async (connection) => {
            addIntegrationConnectionInQueryData(queryClient, connection);
            const hasAccountsNotReadyForSync =
                connection.integrationAccounts?.some(
                    (a) => !a.financialAccount?.entityId,
                );

            if (hasAccountsNotReadyForSync) {
                updateConnectAccountState({
                    isConnecting: false,
                    connectionSaved: true,
                    integrationConnection: connection,
                });
            } else {
                updateConnectAccountState({
                    isConnecting: false,
                });
            }

            queryClient.invalidateQueries({
                queryKey: integrationConnectionQueryKeys.query(
                    workspaceId ?? "",
                ),
            });
            invalidateAccountRelatedQueries({ workspaceId });
            await refreshConnections({
                provider: "mercury",
                connectionId: String(connection.id),
            });
            onSuccess?.(connection);
        },
        onError: () => {
            updateConnectAccountState({
                isConnecting: false,
            });
        },
    });
}
